import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown, Minus, Lightbulb, MessageCircle, Quote, Target, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function ReflectionResponseViewer({ reflectionId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [response, setResponse] = useState(null);
  const [reflection, setReflection] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadResponse();
  }, [reflectionId]);

  const loadResponse = async () => {
    setLoading(true);
    try {
      const responseData = await axios.get(`${API_URL}/weekly-reflections/${reflectionId}/responses`);
      setResponse(responseData.data);

      // The reflection data is included with the response
      // But we can also parse it from the response if needed
      setReflection({
        id: reflectionId,
        // We'll use the questions from the response
      });
    } catch (err) {
      setError('Failed to load response');
      console.error('Error loading response:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[var(--accent)]/30 border-t-[var(--accent)] mx-auto mb-4"></div>
          <p className="text-gray-400">Loading responses...</p>
        </div>
      </div>
    );
  }

  if (error || !response) {
    return (
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-8 text-center">
        <p className="text-gray-400">{error || 'No responses found'}</p>
      </div>
    );
  }

  const analysis = response.ai_analysis;

  const getMomentumIcon = (momentum) => {
    if (momentum === 'gaining') return <TrendingUp className="w-5 h-5 text-green-400" />;
    if (momentum === 'declining') return <TrendingDown className="w-5 h-5 text-red-400" />;
    return <Minus className="w-5 h-5 text-yellow-400" />;
  };

  const getSentimentColor = (sentiment) => {
    if (sentiment === 'positive') return 'text-green-400';
    if (sentiment === 'negative') return 'text-red-400';
    if (sentiment === 'mixed') return 'text-yellow-400';
    return 'text-gray-400';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6">
        <h2 className="text-2xl font-bold text-white mb-2">Client Responses & AI Analysis</h2>
        <p className="text-gray-400">
          Submitted {new Date(response.submitted_at).toLocaleDateString()} at {new Date(response.submitted_at).toLocaleTimeString()}
        </p>
      </div>

      {/* AI Analysis - Preparation Brief */}
      {analysis && (
        <div className="bg-gradient-to-br from-[var(--accent)]/20 to-[var(--accent)]/10 border border-[var(--accent)]/30 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="w-6 h-6 text-[var(--accent)]" />
            <h3 className="text-xl font-semibold text-white">Pre-Session Brief</h3>
          </div>
          <p className="text-white leading-relaxed text-lg">
            {analysis.preparationBrief}
          </p>
        </div>
      )}

      {/* Progress Indicators */}
      {analysis?.progressIndicators && (
        <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-5 h-5 text-[var(--accent)]" />
            <h3 className="text-lg font-semibold text-white">Progress Indicators</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* Momentum */}
            <div className="bg-[var(--bg-primary)] p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                {getMomentumIcon(analysis.progressIndicators.momentum)}
                <span className="text-sm text-gray-400">Momentum</span>
              </div>
              <p className="text-white font-semibold capitalize">{analysis.progressIndicators.momentum}</p>
              <p className="text-xs text-gray-400 mt-1">{analysis.progressIndicators.momentumExplanation}</p>
            </div>

            {/* Improvements */}
            <div className="bg-[var(--bg-primary)] p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-green-400" />
                <span className="text-sm text-gray-400">Improvements</span>
              </div>
              <p className="text-white font-semibold">{analysis.progressIndicators.improvements?.length || 0} noted</p>
            </div>

            {/* Setbacks */}
            <div className="bg-[var(--bg-primary)] p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <TrendingDown className="w-5 h-5 text-orange-400" />
                <span className="text-sm text-gray-400">Challenges</span>
              </div>
              <p className="text-white font-semibold">{analysis.progressIndicators.setbacks?.length || 0} noted</p>
            </div>
          </div>

          {analysis.progressIndicators.improvements?.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-green-400 mb-2">Improvements:</h4>
              <ul className="space-y-1">
                {analysis.progressIndicators.improvements.map((item, i) => (
                  <li key={i} className="text-gray-300 text-sm flex items-start gap-2">
                    <span className="text-green-400 mt-1">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {analysis.progressIndicators.setbacks?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-orange-400 mb-2">Challenges:</h4>
              <ul className="space-y-1">
                {analysis.progressIndicators.setbacks.map((item, i) => (
                  <li key={i} className="text-gray-300 text-sm flex items-start gap-2">
                    <span className="text-orange-400 mt-1">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Emotional Patterns */}
      {analysis?.emotionalPatterns && (
        <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <MessageCircle className="w-5 h-5 text-[var(--accent)]" />
            <h3 className="text-lg font-semibold text-white">Emotional Patterns</h3>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-[var(--bg-primary)] p-4 rounded-lg">
              <p className="text-sm text-gray-400 mb-1">Overall Sentiment</p>
              <p className={`text-lg font-semibold capitalize ${getSentimentColor(analysis.emotionalPatterns.overallSentiment)}`}>
                {analysis.emotionalPatterns.overallSentiment}
              </p>
            </div>
            <div className="bg-[var(--bg-primary)] p-4 rounded-lg">
              <p className="text-sm text-gray-400 mb-1">Energy Level</p>
              <p className="text-lg font-semibold text-white capitalize">{analysis.emotionalPatterns.energyLevel}</p>
            </div>
          </div>

          <p className="text-gray-300 text-sm mb-2">
            <span className="font-semibold text-white">Sentiment Analysis: </span>
            {analysis.emotionalPatterns.sentimentExplanation}
          </p>

          <p className="text-gray-300 text-sm mb-2">
            <span className="font-semibold text-white">Energy Evidence: </span>
            {analysis.emotionalPatterns.energyEvidence}
          </p>

          {analysis.emotionalPatterns.confidenceShift && (
            <p className="text-gray-300 text-sm">
              <span className="font-semibold text-white">Confidence Shift: </span>
              {analysis.emotionalPatterns.confidenceShift}
            </p>
          )}
        </div>
      )}

      {/* Key Quotes */}
      {analysis?.keyQuotes && analysis.keyQuotes.length > 0 && (
        <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Quote className="w-5 h-5 text-[var(--accent)]" />
            <h3 className="text-lg font-semibold text-white">Key Quotes</h3>
          </div>
          <div className="space-y-4">
            {analysis.keyQuotes.map((item, i) => (
              <div key={i} className="bg-[var(--bg-primary)] p-4 rounded-lg border-l-4 border-[var(--accent)]">
                <p className="text-white italic mb-2">"{item.quote}"</p>
                <p className="text-sm text-gray-400">{item.significance}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Suggested Talking Points */}
      {analysis?.suggestedTalkingPoints && analysis.suggestedTalkingPoints.length > 0 && (
        <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <ArrowRight className="w-5 h-5 text-[var(--accent)]" />
            <h3 className="text-lg font-semibold text-white">Suggested Talking Points</h3>
          </div>
          <div className="space-y-4">
            {analysis.suggestedTalkingPoints.map((point, i) => (
              <div key={i} className="bg-[var(--bg-primary)] p-4 rounded-lg">
                <h4 className="text-white font-semibold mb-2">{point.topic}</h4>
                <p className="text-gray-300 text-sm mb-2">
                  <span className="text-gray-400">Why: </span>
                  {point.rationale}
                </p>
                <p className="text-gray-300 text-sm">
                  <span className="text-gray-400">Approach: </span>
                  {point.approachSuggestion}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Client's Actual Responses */}
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Client's Responses</h3>
        <div className="space-y-6">
          {response.responses.map((r, index) => (
            <div key={index} className="bg-[var(--bg-primary)] p-4 rounded-lg">
              <div className="flex items-start gap-3 mb-3">
                <span className="flex-shrink-0 w-8 h-8 bg-[var(--accent)] text-white rounded-full flex items-center justify-center font-semibold text-sm">
                  {index + 1}
                </span>
                <p className="text-white font-medium flex-1">{r.question}</p>
              </div>
              <p className="text-gray-300 whitespace-pre-wrap pl-11">{r.answer}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Recommended Focus Areas */}
      {analysis?.recommendedFocus && analysis.recommendedFocus.length > 0 && (
        <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Recommended Focus for Next Session</h3>
          <ul className="space-y-2">
            {analysis.recommendedFocus.map((focus, i) => (
              <li key={i} className="flex items-start gap-3 text-gray-300">
                <span className="text-[var(--accent)] font-bold">{i + 1}.</span>
                <span>{focus}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default ReflectionResponseViewer;
